import { useState } from 'react'
import { ColumnDef } from '@tanstack/react-table'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { DataTable } from '@/components/ui/data-table'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { useTemplates, useDeleteTemplate } from '@/hooks/useTemplates'
import { Template } from '@/types'
import { Plus, MoreHorizontal, Edit, Trash2, Eye, FileText, Search, Filter, RefreshCw } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import { safeFormatDate } from '@/lib/utils'
import TemplateForm from '@/components/forms/TemplateForm'

const statusMap = {
  PENDING: { label: '待审核', variant: 'warning' as const },
  APPROVED: { label: '已通过', variant: 'success' as const },
  REJECTED: { label: '已拒绝', variant: 'destructive' as const },
}

const typeMap = {
  VERIFICATION: { label: '验证码', color: 'bg-blue-100 text-blue-800' },
  VERIFY_CODE: { label: '验证码', color: 'bg-blue-100 text-blue-800' },
  NOTIFICATION: { label: '通知', color: 'bg-green-100 text-green-800' },
  MARKETING: { label: '营销', color: 'bg-purple-100 text-purple-800' },
}

export default function Templates() {
  const [page, setPage] = useState(0)
  const [searchFilters, setSearchFilters] = useState({
    templateName: '',
    status: 'ALL',
    templateType: 'ALL'
  })
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null)
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [showEditDialog, setShowEditDialog] = useState(false)
  const [showDetailDialog, setShowDetailDialog] = useState(false)

  const { toast } = useToast()
  const { data: templatesData, isLoading } = useTemplates({ 
    page, 
    size: 10, 
    search: searchFilters.templateName || undefined,
    status: searchFilters.status !== 'ALL' ? searchFilters.status : undefined,
    templateType: searchFilters.templateType !== 'ALL' ? searchFilters.templateType : undefined
  })
  const deleteTemplateMutation = useDeleteTemplate()
  
  // 处理搜索
  const handleSearch = () => {
    setPage(0) // 重置到第一页
  }

  // 处理重置
  const handleReset = () => {
    setSearchFilters({
      templateName: '',
      status: 'ALL',
      templateType: 'ALL'
    })
    setPage(0)
  }

  const handleDelete = async (template: Template) => {
    if (confirm(`确定要删除模板 ${template.templateName} 吗？`)) {
      try {
        await deleteTemplateMutation.mutateAsync(template.id)
        toast({
          title: '删除成功',
          description: `模板 ${template.templateName} 已被删除`,
        })
      } catch (error) {
        toast({
          title: '删除失败',
          description: '请稍后重试',
          variant: 'destructive',
        })
      }
    }
  }

  const columns: ColumnDef<Template>[] = [
    {
      accessorKey: 'templateName',
      header: '模板名称',
    },
    {
      accessorKey: 'signatureName',
      header: '关联签名',
      cell: ({ row }) => {
        const signatureName = row.original.signatureName
        return (
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
            <span className="text-sm">
              {signatureName ? `【${signatureName}】` : '未关联签名'}
            </span>
          </div>
        )
      },
    },
    {
      accessorKey: 'templateType',
      header: '模板类型',
      cell: ({ row }) => {
        const type = typeMap[row.getValue('templateType') as keyof typeof typeMap] || { label: '未知', color: 'bg-gray-100 text-gray-800' }
        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${type.color}`}>
            {type.label}
          </span>
        )
      },
    },
    {
      accessorKey: 'templateContent',
      header: '模板内容',
      cell: ({ row }) => {
        const content = row.getValue('templateContent') as string
        if (!content) {
          return <span className="text-gray-500 text-sm">无内容</span>
        }
        return (
          <div className="max-w-xs truncate" title={content}>
            {content}
          </div>
        )
      },
    },
    {
      accessorKey: 'status',
      header: '审核状态',
      cell: ({ row }) => {
        const status = statusMap[row.getValue('status') as keyof typeof statusMap] || { label: '未知', variant: 'secondary' as const }
        return <Badge variant={status.variant}>{status.label}</Badge>
      },
    },
    {
      accessorKey: 'variables',
      header: '变量',
      cell: ({ row }) => {
        const variables = row.getValue('variables') as string[] | null
        if (!variables || variables.length === 0) {
          return <span className="text-gray-500 text-sm">无变量</span>
        }
        return (
          <div className="flex gap-1 flex-wrap">
            {variables.slice(0, 3).map((variable, index) => (
              <Badge key={index} variant="outline" className="text-xs">
                {variable}
              </Badge>
            ))}
            {variables.length > 3 && (
              <Badge variant="outline" className="text-xs">
                +{variables.length - 3}
              </Badge>
            )}
          </div>
        )
      },
    },
    {
      accessorKey: 'createdAt',
      header: '创建时间',
      cell: ({ row }) => {
        return safeFormatDate(row.getValue('createdAt'), 'yyyy-MM-dd', '未知时间')
      },
    },
    {
      id: 'actions',
      header: '操作',
      cell: ({ row }) => {
        const template = row.original
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => {
                  setSelectedTemplate(template)
                  setShowDetailDialog(true)
                }}
              >
                <Eye className="mr-2 h-4 w-4" />
                查看详情
              </DropdownMenuItem>
              {template.status !== 'APPROVED' && (
                <DropdownMenuItem
                  onClick={() => {
                    setSelectedTemplate(template)
                    setShowEditDialog(true)
                  }}
                >
                  <Edit className="mr-2 h-4 w-4" />
                  编辑
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onClick={() => handleDelete(template)}
                className="text-red-600"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                删除
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )
      },
    },
  ]

  return (
    <div className="flex-1 space-y-4 p-8 pt-6">
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-bold tracking-tight">模板管理</h2>
        <div className="flex space-x-2">
          <Button variant="outline" onClick={() => window.location.reload()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            刷新
          </Button>
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                申请模板
              </Button>
            </DialogTrigger>
          <DialogContent className="sm:max-w-[600px] max-h-[90vh] flex flex-col w-[95vw] h-[90vh] !grid-cols-1 !gap-0">
            <DialogHeader className="flex-shrink-0">
              <DialogTitle>申请新模板</DialogTitle>
              <DialogDescription>
                创建新的短信模板，提交后等待审核
              </DialogDescription>
            </DialogHeader>
            <div className="flex-1 min-h-0 overflow-hidden">
              <TemplateForm
                onSuccess={() => {
                  setShowCreateDialog(false)
                  toast({
                    title: '申请成功',
                    description: '模板已提交，等待审核',
                  })
                }}
              />
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* 搜索筛选区域 */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Filter className="h-5 w-5 mr-2" />
            查询条件
          </CardTitle>
          <CardDescription>设置查询条件来筛选模板记录</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>模板名称</Label>
              <Input
                placeholder="输入模板名称"
                value={searchFilters.templateName}
                onChange={(e) => setSearchFilters(prev => ({ ...prev, templateName: e.target.value }))}
              />
            </div>
            
            <div className="space-y-2">
              <Label>审核状态</Label>
              <Select value={searchFilters.status} onValueChange={(value) => setSearchFilters(prev => ({ ...prev, status: value }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">全部状态</SelectItem>
                  <SelectItem value="PENDING">待审核</SelectItem>
                  <SelectItem value="APPROVED">已通过</SelectItem>
                  <SelectItem value="REJECTED">已拒绝</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>模板类型</Label>
              <Select value={searchFilters.templateType} onValueChange={(value) => setSearchFilters(prev => ({ ...prev, templateType: value }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">全部类型</SelectItem>
                  <SelectItem value="VERIFICATION">验证码</SelectItem>
                  <SelectItem value="NOTIFICATION">通知</SelectItem>
                  <SelectItem value="MARKETING">营销</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="flex justify-end space-x-2 mt-4">
            <Button variant="outline" onClick={handleReset}>
              重置
            </Button>
            <Button onClick={handleSearch}>
              <Search className="h-4 w-4 mr-2" />
              查询
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>我的模板</CardTitle>
          <CardDescription>
            管理您的短信模板和审核状态
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="text-sm text-muted-foreground">加载中...</div>
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={templatesData?.records || []}
              pagination={{
                page: page + 1, // 转换为1-based
                totalPages: templatesData?.pages ?? 0,
                totalItems: templatesData?.total ?? 0,
                itemsPerPage: 10,
                onPageChange: (newPage) => setPage(newPage - 1), // 转换回0-based
                onItemsPerPageChange: () => {}, // 固定大小为10
              }}
            />
          )}
        </CardContent>
      </Card>

      {/* 编辑模板对话框 */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] flex flex-col w-[95vw] h-[90vh] !grid-cols-1 !gap-0" style={{ height: '90vh', maxHeight: '90vh' }}>
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>编辑模板</DialogTitle>
            <DialogDescription>
              修改模板信息
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 min-h-0 overflow-hidden">
            {selectedTemplate && (
              <TemplateForm
                template={selectedTemplate}
                onSuccess={() => {
                  setShowEditDialog(false)
                  setSelectedTemplate(null)
                  toast({
                    title: '更新成功',
                    description: '模板信息已更新',
                  })
                }}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* 模板详情对话框 */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="sm:max-w-[700px]">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <FileText className="mr-2 h-5 w-5" />
              模板详情
            </DialogTitle>
          </DialogHeader>
          {selectedTemplate && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">模板名称</Label>
                  <p className="text-sm text-muted-foreground">{selectedTemplate.templateName}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium">模板类型</Label>
                  <div className="mt-1">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${typeMap[selectedTemplate.templateType]?.color || 'bg-gray-100 text-gray-800'}`}>
                      {typeMap[selectedTemplate.templateType]?.label || '未知'}
                    </span>
                  </div>
                </div>
              </div>
              
              <div>
                <Label className="text-sm font-medium">关联签名</Label>
                <div className="flex items-center space-x-2 mt-1">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <span className="text-sm text-muted-foreground">
                    {selectedTemplate.signatureName ? `【${selectedTemplate.signatureName}】` : '未关联签名'}
                  </span>
                </div>
              </div>
              
              <div>
                <Label className="text-sm font-medium">模板内容</Label>
                <p className="text-sm text-muted-foreground mt-1 p-3 bg-gray-50 rounded-lg">
                  {selectedTemplate.templateContent}
                </p>
              </div>
              
              <div>
                <Label className="text-sm font-medium">模板变量</Label>
                <div className="mt-1 flex gap-2 flex-wrap">
                  {selectedTemplate.variables && selectedTemplate.variables.length > 0 ? (
                    selectedTemplate.variables.map((variable, index) => (
                      <Badge key={index} variant="outline">
                        {variable}
                      </Badge>
                    ))
                  ) : (
                    <span className="text-sm text-muted-foreground">无变量</span>
                  )}
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">审核状态</Label>
                  <div className="mt-1">
                    <Badge variant={statusMap[selectedTemplate.status]?.variant || 'secondary'}>
                      {statusMap[selectedTemplate.status]?.label || '未知'}
                    </Badge>
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium">创建时间</Label>
                  <p className="text-sm text-muted-foreground">
                    {safeFormatDate(selectedTemplate.createdAt, 'yyyy-MM-dd HH:mm:ss')}
                  </p>
                </div>
              </div>
              
              {selectedTemplate.reviewComment && (
                <div>
                  <Label className="text-sm font-medium">审核意见</Label>
                  <p className="text-sm text-muted-foreground mt-1 p-3 bg-gray-50 rounded-lg">
                    {selectedTemplate.reviewComment}
                  </p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}